import { type Event, EventListener } from "silver";

export interface BookChangeEvent extends Event {
	sender: Book;
	property: "onMyList"|"myRating"|"minimumGradeBookAccess"|"minGradeBookLastModified";
}


const PAGECOUNT_SHORT  = 24;
const PAGECOUNT_MEDIUM = 40;
const PAGECOUNT_LONG   = 100;

const RUNTIME_SECONDS_SHORT  = 15 * 60;
const RUNTIME_SECONDS_MEDIUM = 30 * 60;
const RUNTIME_SECONDS_LONG   = 60 * 60;

export enum BookLength {
	UNKNOWN = "unknown",
	SHORT = "short",
	MEDIUM = "medium",
	LONG = "long",
	EPIC = "epic"
}

export function getBookLength(book: Book): BookLength {
	if ((book.pagecount !== null && book.pagecount > PAGECOUNT_LONG) ||
		(book.audioRuntimeSecs !== null && book.audioRuntimeSecs > RUNTIME_SECONDS_LONG)) {
		return BookLength.EPIC;
	}
	if ((book.pagecount !== null && book.pagecount > PAGECOUNT_MEDIUM) ||
		(book.audioRuntimeSecs !== null && book.audioRuntimeSecs > RUNTIME_SECONDS_MEDIUM)) {
		return BookLength.LONG;
	}
	if ((book.pagecount !== null && book.pagecount > PAGECOUNT_SHORT) ||
		(book.audioRuntimeSecs !== null && book.audioRuntimeSecs > RUNTIME_SECONDS_SHORT)) {
		return BookLength.MEDIUM;
	}
	if (book.pagecount !== null || book.audioRuntimeSecs !== null) {
		return BookLength.SHORT;
	}
	return BookLength.UNKNOWN;
}

export class Book {

	public readonly onChange = EventListener.create<BookChangeEvent>("change");

	constructor(
		public readonly archiveCode: string,
		public readonly bookPublisherId: number,
		public readonly bookPublisherTitle: string,
		public readonly bookPublisherLogo: string,
		public readonly bookId: number,
		public readonly bookBrandId: number,
		public readonly bookBrandTitle: string,
		public readonly bookSeriesId: number,
		public readonly bookSeriesTitle: string,
		public readonly title: string,
		public readonly titleShort: string,
		public readonly description: string,
		public readonly author: string|null,
		public readonly illustrator: string|null,
		public readonly pagecount: number|null,
		public readonly wordcount: number|null,
		public readonly audioRuntimeSecs: number|null,
		public readonly audioRuntimeFriendly: string,
		public readonly readingLevel: string|null,
		public readonly guidedReadingLevel: string|null,
		public readonly arQuiz: null,
		public readonly arPoints: null,
		public readonly arQuizLevel: number|null,
		public readonly hasBookQuiz: boolean,
		public readonly scholasticReadingCount: number|null,
		public readonly cefrLevelId: number|null,
		public readonly interestLevel: string|null,
		public readonly lexileLevel: number|null,
		public readonly lexileLabel: string|null,
		public readonly minUserGradeLevelId: number|null,
		public readonly maxUserGradeLevelId: number|null,
		public readonly copyright: number|null,
		public readonly fiction: boolean,
		public readonly graphic: boolean,
		public readonly version: number,
		public readonly deleted: boolean,
		public readonly dateAdded: string|null,
		public readonly coverUrl: string,
		public readonly coverUrlCropped: string,
		private minimumGradeBookAccessValue: number|null,
		private minGradeBookLastModifiedValue: string,
		public readonly manifestXml: string,
		public readonly languages: string,
		public readonly languageIds: number[],
		public readonly avgReview: number,
		public readonly myRating: number,
		public readonly reviewsLink: string,
		public onMyList: boolean,
		public readonly accountMinimumGradeBookAccess: number|null|undefined,
		public readonly accountMinGradeBookLastModified: string|undefined
	) { }

	get minGradeBookLastModified(): string {
		return this.minGradeBookLastModifiedValue;
	}

	set minGradeBookLastModified(value: string) {
		if (value == this.minGradeBookLastModifiedValue) {
			return;
		}
		this.minGradeBookLastModifiedValue = value;
		this.onChange.trigger({ sender: this, property: "minGradeBookLastModified" });
	}

	get minimumGradeBookAccess(): number|null {
		return this.minimumGradeBookAccessValue;
	}

	set minimumGradeBookAccess(value: number|null) {
		if (value == this.minimumGradeBookAccessValue) {
			return;
		}
		this.minimumGradeBookAccessValue = value;
		this.onChange.trigger({ sender: this, property: "minimumGradeBookAccess" });
	}
}
